import {
	isDashboardAnalystEnabled,
	isDealScreeningEnabled,
	isMonitoringEnabled,
	isWorkflowsEnabled,
} from "./featureFlags";

/**
 * Where a chat lives (DOP-441, repointed at the unified IA by DOP-478).
 *
 * A workflow thread's home is the workflow: its turns only make sense with
 * the grid beside them, because every question is asked about what is on
 * screen and the rail is what sends that. Opened at /chat it would answer
 * without the workflow. So the sidebar sends it to the workflow's own page
 * with the rail on that thread — unless the rail is off on this deployment, in
 * which case /chat is the only place it can be read.
 */
export function chatHref(chat: {
	id: string;
	surface?: string | null;
	surface_state?: {
		scope?: { property_key?: string | null; rollup?: string | null } | null;
		grain?: string | null;
		run_public_id?: string | null;
		run_id?: string | number | null;
		tape_id?: string | null;
		mapping_view?: string | null;
		workflow_id?: string | null;
		deal_name?: string | null;
		tab?: string | null;
	} | null;
}): string {
	// A monitoring thread's home is its run page (DOP-470): the rail asked
	// about one borrower-quarter's ledger, and the answers only read next to
	// it. The tab is the one the question was asked from.
	const runId = chat.surface_state?.run_public_id;
	if (chat.surface === "monitoring_run" && runId && isMonitoringEnabled()) {
		const params = new URLSearchParams({ [RUN_THREAD_PARAM]: chat.id });
		if (chat.surface_state?.tab) params.set(RUN_TAB_PARAM, chat.surface_state.tab);
		return `/workflows/monitoring/runs/${encodeURIComponent(runId)}?${params.toString()}`;
	}
	// A deal screening thread's home is its run page, on the tab it was asked
	// from: the rail asked about one memo version, and `run_id` is the run the
	// last turn's descriptor named.
	const screeningRunId = chat.surface_state?.run_id;
	if (
		chat.surface === "deal_screening_run" &&
		screeningRunId &&
		isDealScreeningEnabled()
	) {
		const params = new URLSearchParams({ [RUN_THREAD_PARAM]: chat.id });
		if (chat.surface_state?.tab) params.set(RUN_TAB_PARAM, chat.surface_state.tab);
		return `/workflows/deal-screening/runs/${encodeURIComponent(screeningRunId)}?${params.toString()}`;
	}
	// A loan tape thread's home is the tape's page, on the tab and the run it
	// was last asked from. A thread belongs to the tape, not to one run, so an
	// absent run id is the newest run and the link says nothing about it.
	const tapeId = chat.surface_state?.tape_id;
	if (chat.surface === "loan_tape_run" && tapeId && isWorkflowsEnabled()) {
		const params = new URLSearchParams({ [RUN_THREAD_PARAM]: chat.id });
		if (chat.surface_state?.tab) params.set(RUN_TAB_PARAM, chat.surface_state.tab);
		const tapeRunId = chat.surface_state?.run_id;
		if (tapeRunId) params.set(LOAN_TAPE_RUN_PARAM, String(tapeRunId));
		// Columns, Map or SQL, when the question was asked from the Mapping tab.
		const mappingView = chat.surface_state?.mapping_view;
		if (mappingView) params.set(LOAN_TAPE_MAPPING_VIEW_PARAM, mappingView);
		return `/workflows/loan-tape/${encodeURIComponent(tapeId)}?${params.toString()}`;
	}
	// A portfolio data thread's home is the deal's tables, on the tab it was
	// last asked from.
	const portfolioDeal = chat.surface_state?.deal_name;
	if (chat.surface === "portfolio_data_run" && portfolioDeal && isWorkflowsEnabled()) {
		const params = new URLSearchParams({ [RUN_THREAD_PARAM]: chat.id });
		if (chat.surface_state?.tab) params.set(RUN_TAB_PARAM, chat.surface_state.tab);
		return `/workflows/portfolio-data/${encodeURIComponent(portfolioDeal)}?${params.toString()}`;
	}
	if (chat.surface === "kpi_dashboard" && isDashboardAnalystEnabled()) {
		const params = new URLSearchParams({ [DASHBOARD_THREAD_PARAM]: chat.id });
		// The view the thread was last asked from, so the workflow opens on
		// the same grid and grain rather than its default. It travels as
		// `?view=` and not `?tab=` (decision D6): `?tab=` is the shell's own —
		// Data, Needs review, History, Settings — and a property name in it
		// would name a tab no workflow has.
		const scope = chat.surface_state?.scope;
		if (scope) {
			params.set(DASHBOARD_VIEW_PARAM, scope.property_key ?? DASHBOARD_VIEW_PORTFOLIO);
		}
		if (chat.surface_state?.grain) {
			params.set(DASHBOARD_GRAIN_PARAM, chat.surface_state.grain);
		}
		// Threads recorded before the surface carried a workflow id, and any
		// asked of the portfolio rather than of one workflow, have no page of
		// their own to open — the index is where their workflow is found.
		const workflowId = chat.surface_state?.workflow_id;
		if (!workflowId) return `/workflows?${params.toString()}`;
		return `/workflows/kpi/${encodeURIComponent(workflowId)}?${params.toString()}`;
	}
	return `/chat/${chat.id}`;
}

/** The query keys a workflow reads its thread and view from. */
export const DASHBOARD_THREAD_PARAM = "thread";
export const DASHBOARD_VIEW_PARAM = "view";
export const DASHBOARD_GRAIN_PARAM = "grain";
/** The view value that means the portfolio rollup rather than a property. */
export const DASHBOARD_VIEW_PORTFOLIO = "portfolio";

/** The query keys the monitoring run page reads its thread and tab from. */
export const RUN_THREAD_PARAM = "thread";
export const RUN_TAB_PARAM = "tab";
/** The query key the loan tape page reads its shown run from. */
export const LOAN_TAPE_RUN_PARAM = "run";
/** The query key the loan tape page reads its Mapping view from. */
export const LOAN_TAPE_MAPPING_VIEW_PARAM = "view";
